import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView, BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { useCallback, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors, spacing } from '@/constants/theme';
import { useFormSheet } from '@/hooks/useFormSheet';
import { usersService } from '@/services/users';
import { authStore } from '@/stores/auth.store';

import { Button } from './Button';
import { Heading } from './Heading';
import { Input } from './Input';
import { Text } from './Text';

export interface EditNameSheetProps {
  onClose: () => void;
}

// Rendered conditionally by parents (parent gates with
// `{editNameOpen && <EditNameSheet ... />}`) so the sheet doesn't peek
// on initial mount. Uses `enableDynamicSizing` so the sheet auto-sizes
// to its content height (Heading + 2 Inputs + Save button) and the
// user can drag up to the full screen.
export function EditNameSheet({ onClose }: EditNameSheetProps) {
  // Two snap points: opens at 65% (tall enough for Heading + 2 Inputs +
  // Save), expands to 95% when a field is focused (room for the
  // keyboard; see useFormSheet). Tried
  // `enableDynamicSizing` alone — doesn't measure content correctly
  // when the sheet contains plain RN TextInputs (gorhom expects
  // BottomSheetTextInput for proper measurement + keyboard handling).
  // Sticking with fixed snap points + fillParent keyboardBehavior per
  // gorhom v5 docs for sheets that have text inputs.
  const snapPoints = useMemo<(string | number)[]>(() => ['65%', '95%'], []);
  // Expands on focus and dismisses the keyboard on close (see useFormSheet).
  const { sheetRef, expandOnFocus, handleChange, close } = useFormSheet(onClose, snapPoints.length - 1);

  // Respect the device's top safe area so the sheet at max extension
  // doesn't slide under the status bar / dynamic island.
  const { top: topInset } = useSafeAreaInsets();

  const [firstName, setFirstName] = useState(authStore.user?.first_name ?? '');
  const [lastName, setLastName] = useState(authStore.user?.last_name ?? '');
  const [isSaving, setIsSaving] = useState(false);
  // Ref, not state: a double tap can fire twice before the re-render that
  // disables Save.
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<'first' | 'last' | null>(null);

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
    ),
    [],
  );

  const canSave = firstName.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || inFlight.current) return;
    inFlight.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await usersService.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      await authStore.refreshMe();
      close();
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      inFlight.current = false;
      setIsSaving(false);
    }
  };

  return (
    <BottomSheet
      ref={sheetRef}
      index={0}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      enablePanDownToClose
      topInset={topInset}
      backdropComponent={renderBackdrop}
      onChange={handleChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
      keyboardBehavior="fillParent"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
    >
      <BottomSheetScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        testID="edit-name-sheet"
      >
        <Heading size="2xl">Edit name</Heading>

        {error && (
          <Text variant="caption" color="danger" testID="edit-name-error">
            {error}
          </Text>
        )}

        <Input
          label="First name"
          value={firstName}
          onChangeText={setFirstName}
          autoCapitalize="words"
          returnKeyType="next"
          onFocus={() => {
            setFocusedField('first');
            expandOnFocus();
          }}
          onBlur={() => setFocusedField(null)}
          focused={focusedField === 'first'}
          textInputComponent={BottomSheetTextInput}
          testID="input-first-name"
        />

        <Input
          label="Last name"
          value={lastName}
          onChangeText={setLastName}
          autoCapitalize="words"
          returnKeyType="done"
          onFocus={() => {
            setFocusedField('last');
            expandOnFocus();
          }}
          onBlur={() => setFocusedField(null)}
          focused={focusedField === 'last'}
          textInputComponent={BottomSheetTextInput}
          testID="input-last-name"
        />

        <Button
          label="Save"
          variant="primary"
          fullWidth
          loading={isSaving}
          disabled={!canSave}
          onPress={handleSave}
          testID="save-name-btn"
        />
      </BottomSheetScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  handle: {
    backgroundColor: colors.border,
  },
  background: {
    backgroundColor: colors.surface,
  },
  content: {
    padding: spacing.lg,
    gap: spacing.base,
  },
});
